import styled from "@emotion/styled";
import { Outlet, useNavigate, useOutlet, useParams } from "react-router";
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@store/auth.store";
import { useUIStore } from "@store/ui.store";
import { useAddContactDialogStore } from "@store/add-contact-dialog.store";
import {
  ChatSidebar,
  ChatSidebarSkeleton,
} from "@features/Chat/components/ChatSidebar";
import { SettingsModal } from "@features/Navbar/SettingsModal";
import { useSettingsModalFromUrl } from "@features/Navbar/useSettingsModalFromUrl";
import { NewContactDialog } from "@features/Contacts";
import { ImportContactsCsvDialog } from "@features/Contacts/components/ImportContactsCsvDialog/ImportContactsCsvDialog";
import { listContactsQueryKey } from "@shared/generated/crm";
import {
  contactActivityQueryKey,
  countContactsQueryKey,
} from "@shared/generated/radar";
import { useChatWidgetLogic } from "@shared/hooks/useChatWidgetLogic";
import { useAppName } from "@shared/hooks/useAppName";
import { useAppDescription } from "@shared/hooks/useAppDescription";
import { RadarOnboardingGate } from "@modules/radar/shared/components/RadarOnboardingDialog";
import { Helmet } from "react-helmet-async";

const AppContainer = styled.div`
  display: flex;
  height: 100vh;
  max-height: 100vh;
  @supports (height: 100dvh) {
    height: 100dvh;
    max-height: 100dvh;
  }
  background-color: ${(p) => p.theme.colors.background};

  ${(p) => p.theme.media.laptopDown} {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
  }
`;

const MainContent = styled.main`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: transparent;
`;

type MainLayoutProps = {
  children?: React.ReactNode;
};

export function MainLayout({ children }: MainLayoutProps) {
  const navigate = useNavigate();
  const outlet = useOutlet();
  const { threadId: currentThread } = useParams<{ threadId: string }>();
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const { chatWidget, resetChatWidget } = useChatWidgetLogic();
  const appName = useAppName();
  const appDescription = useAppDescription();
  const queryClient = useQueryClient();
  const settingsModal = useUIStore((state) => state.settingsModal);
  const { handleCloseSettingsModal } = useSettingsModalFromUrl();
  const addContactDialogMode = useAddContactDialogStore((state) => state.mode);
  const closeAddContactDialog = useAddContactDialogStore(
    (state) => state.closeAddContactDialog,
  );
  const navigateToThread = useCallback(
    (threadId: string | null) => {
      navigate(threadId ? `/chat/${threadId}` : "/new");
    },
    [navigate],
  );

  const onNewChatClick = useCallback(() => {
    if (chatWidget?.temporary) {
      resetChatWidget();
    }
    navigate("/new");
  }, [navigate, chatWidget, resetChatWidget]);

  return (
    <AppContainer>
      <Helmet>
        <title>{appName}</title>
        <meta name="description" content={appDescription} />
      </Helmet>
      {isAuthenticated ? (
        <ChatSidebar
          currentThread={currentThread}
          navigateToThread={navigateToThread}
          onNewChatClick={onNewChatClick}
          userAvatar={user.avatar}
          userName={user.name}
          userFirstName={user.firstName}
          userEmail={user.email}
          userRoles={user.roles}
          userSubscriptionStatus={user.subscriptionStatus}
        />
      ) : (
        <ChatSidebarSkeleton />
      )}
      <MainContent>{children ?? outlet ?? <Outlet />}</MainContent>
      {isAuthenticated && (
        <>
          <SettingsModal
            open={settingsModal.open}
            onClose={handleCloseSettingsModal}
            initialTab={settingsModal.tab}
            highlightAppId={settingsModal.highlightAppId}
            key={`${settingsModal.tab}-${settingsModal.open}`}
          />
          <NewContactDialog
            open={addContactDialogMode === "manual"}
            onClose={closeAddContactDialog}
          />
          <ImportContactsCsvDialog
            open={addContactDialogMode === "csv"}
            onClose={closeAddContactDialog}
            onImported={() => {
              void queryClient.invalidateQueries({
                queryKey: listContactsQueryKey(),
              });
              void queryClient.invalidateQueries({
                queryKey: countContactsQueryKey(),
              });
              void queryClient.invalidateQueries({
                queryKey: contactActivityQueryKey(),
              });
            }}
          />
        </>
      )}
      <RadarOnboardingGate />
    </AppContainer>
  );
}
